import React, { useCallback, useState } from "react";
import Layout from "../../../Layouts/Layout";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { useDropzone } from "react-dropzone";
import { useForm } from "@inertiajs/inertia-react";
import { Inertia } from "@inertiajs/inertia";
import { SelectStyles } from "../../../styles/ReactSelect";
import { CameraIcon, XMarkIcon } from "@heroicons/react/24/outline";
import StarRating from "../../../Components/Domestic/StarRating";

const SOCIAL_PLATFORMS = [
  { value: "facebook", label: "Facebook" },
  { value: "instagram", label: "Instagram" },
  { value: "x", label: "X / Twitter" },
  { value: "tiktok", label: "TikTok" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "other", label: "Other" },
];

// Tradesman-side configuration for Tradesman Domestic: which trades they
// take domestic work in, their free primary postcode district, paid extra
// districts (£10/month each, quantity-billed via Stripe), and the profile
// customers see when browsing tradespeople matching their job (website,
// social links, portfolio photos).
const ProfileSetup = ({
  profile,
  profile_trades,
  primary_postcode_district,
  extra_postcodes,
  subscription,
  images,
  primary_postcode_locked_until,
  average_rating,
  reviews,
  trades,
  monthly_price_per_postcode,
}) => {
  const { data, setData, put, processing } = useForm({
    active: profile ? profile.active : true,
    primary_postcode_district_id: profile
      ? profile.primary_postcode_district_id
      : null,
    trade_ids: (profile_trades || []).map((t) => t.id),
    website_url: profile?.website_url || "",
    work_description: profile?.work_description || "",
    social_links:
      profile?.social_links?.length > 0
        ? profile.social_links
        : [{ platform: "facebook", url: "" }],
    images: [],
  });

  const [extraPostcodeChoice, setExtraPostcodeChoice] = useState(null);
  const [previews, setPreviews] = useState([]);

  const tradeOptions = trades.map((t) => ({ value: t.id, label: t.name }));

  const districtLabel = (d) =>
    d.region ? `${d.code} — ${d.region}` : d.code;

  const loadDistricts = (inputValue, callback) => {
    if (!inputValue || inputValue.length < 1) {
      callback([]);
      return;
    }
    fetch(
      `/domestic/postcode_districts?q=${encodeURIComponent(inputValue)}`,
      { headers: { Accept: "application/json" } }
    )
      .then((r) => r.json())
      .then((districts) =>
        callback(
          districts.map((d) => ({ value: d.id, label: districtLabel(d) }))
        )
      )
      .catch(() => callback([]));
  };

  // See RequestForm.js for why this must be the functional setData form —
  // onDrop is memoized with an empty dependency array, so a plain
  // setData(key, value) would close over a stale snapshot of `data`.
  const onDrop = useCallback((acceptedFiles) => {
    const withPreviews = acceptedFiles.map((file) =>
      Object.assign(file, { preview: URL.createObjectURL(file) })
    );
    setPreviews((prev) => [...prev, ...withPreviews]);
    setData((prev) => ({ ...prev, images: [...prev.images, ...acceptedFiles] }));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
  });

  function removeNewImage(index) {
    setPreviews((prev) => prev.filter((_, i) => i !== index));
    setData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  }

  function removeExistingImage(imageId) {
    Inertia.delete(`/domestic/tradesman_profile/images/${imageId}`);
  }

  function addSocialLink() {
    setData("social_links", [
      ...data.social_links,
      { platform: "facebook", url: "" },
    ]);
  }

  function updateSocialLink(index, field, value) {
    setData(
      "social_links",
      data.social_links.map((link, i) =>
        i === index ? { ...link, [field]: value } : link
      )
    );
  }

  function removeSocialLink(index) {
    setData(
      "social_links",
      data.social_links.filter((_, i) => i !== index)
    );
  }

  function submit(e) {
    e.preventDefault();
    put("/domestic/tradesman_profile");
  }

  function addExtraPostcode() {
    if (!extraPostcodeChoice) return;
    Inertia.post("/domestic/postcode_subscriptions", {
      postcode_district_id: extraPostcodeChoice.value,
    });
    setExtraPostcodeChoice(null);
  }

  function removeExtraPostcode(subscriptionId) {
    Inertia.delete(`/domestic/postcode_subscriptions/${subscriptionId}`);
  }

  function undoRemoval(postcodeDistrictId) {
    Inertia.post("/domestic/postcode_subscriptions", {
      postcode_district_id: postcodeDistrictId,
    });
  }

  function resumeCheckout() {
    Inertia.post("/domestic/postcode_subscriptions/checkout");
  }

  return (
    <Layout title="Tradesman Domestic — My Profile">
      <div className="mt-6 max-w-2xl space-y-8">
        <form
          onSubmit={submit}
          className="space-y-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Trades you take domestic work in
            </label>
            <p className="text-xs text-gray-500 mb-1">
              You will only see customer requests matching these trades.
            </p>
            <Select
              isMulti
              options={tradeOptions}
              styles={SelectStyles}
              value={tradeOptions.filter((o) =>
                data.trade_ids.includes(o.value)
              )}
              onChange={(selected) =>
                setData(
                  "trade_ids",
                  (selected || []).map((o) => o.value)
                )
              }
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Primary postcode district (free)
            </label>
            <p className="text-xs text-gray-500 mb-1">
              e.g. KT22 — currently KT postcodes only. You will see customer
              requests in this district at no cost. You can change this once
              a month.
            </p>
            {primary_postcode_locked_until ? (
              <p className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-500">
                {districtLabel(primary_postcode_district)} — you can next
                change this on {primary_postcode_locked_until}.
              </p>
            ) : (
              <AsyncSelect
                styles={SelectStyles}
                loadOptions={loadDistricts}
                defaultOptions={[]}
                placeholder="Search a KT postcode district…"
                defaultValue={
                  primary_postcode_district
                    ? {
                        value: primary_postcode_district.id,
                        label: districtLabel(primary_postcode_district),
                      }
                    : null
                }
                onChange={(selected) =>
                  setData(
                    "primary_postcode_district_id",
                    selected ? selected.value : null
                  )
                }
              />
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Describe your work
            </label>
            <p className="text-xs text-gray-500 mb-1">
              Shown to homeowners browsing tradespeople for their job.
              What do you do, and what kind of domestic work do you take on?
            </p>
            <textarea
              rows={5}
              value={data.work_description}
              onChange={(e) => setData("work_description", e.target.value)}
              placeholder="e.g. Family-run plastering firm covering Kingston and the surrounding KT postcodes. We take on repairs, skim coats, and full room replasters…"
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Website
            </label>
            <p className="text-xs text-gray-500 mb-1">
              Shown to customers browsing tradespeople for their job.
              Optional.
            </p>
            <input
              type="url"
              value={data.website_url}
              onChange={(e) => setData("website_url", e.target.value)}
              placeholder="https://www.yourbusiness.co.uk"
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Social media links
            </label>
            <p className="text-xs text-gray-500 mb-1">Optional.</p>
            <div className="space-y-2">
              {data.social_links.map((link, index) => (
                <div key={index} className="flex items-center gap-2">
                  <select
                    value={link.platform}
                    onChange={(e) =>
                      updateSocialLink(index, "platform", e.target.value)
                    }
                    className="w-36 flex-shrink-0 rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                  >
                    {SOCIAL_PLATFORMS.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                  <input
                    type="url"
                    value={link.url}
                    onChange={(e) =>
                      updateSocialLink(index, "url", e.target.value)
                    }
                    placeholder="https://…"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => removeSocialLink(index)}
                    className="flex-shrink-0 text-gray-400 hover:text-red-500"
                    aria-label="Remove link"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addSocialLink}
              className="mt-2 text-sm font-medium text-orange-600 hover:text-orange-500"
            >
              + Add another link
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Portfolio photos
            </label>
            <p className="text-xs text-gray-500 mb-1">
              Photos of past work, shown on your profile. Optional.
            </p>

            {images.length > 0 && (
              <ul className="mb-3 grid grid-cols-3 gap-3 sm:grid-cols-4">
                {images.map((img) => (
                  <li
                    key={img.id}
                    className="group relative aspect-square overflow-hidden rounded-md border border-gray-200 bg-white shadow-sm"
                  >
                    <img
                      src={img.thumb_url}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => removeExistingImage(img.id)}
                      className="absolute top-1 right-1 flex h-6 w-6 items-center justify-center rounded-full bg-gray-900/70 text-white opacity-0 transition-opacity group-hover:opacity-100"
                      aria-label="Remove photo"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <div
              {...getRootProps()}
              className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-8 text-center transition-colors ${
                isDragActive
                  ? "border-orange-500 bg-orange-50"
                  : "border-gray-300 bg-gray-50 hover:border-orange-400 hover:bg-orange-50/50"
              }`}
            >
              <input {...getInputProps()} />
              <CameraIcon className="h-8 w-8 text-gray-400" />
              <p className="mt-2 text-sm text-gray-600">
                {isDragActive
                  ? "Drop your photos here…"
                  : "Drag photos here, or click to browse"}
              </p>
              <p className="mt-0.5 text-xs text-gray-400">JPG or PNG</p>
            </div>

            {previews.length > 0 && (
              <ul className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-4">
                {previews.map((file, index) => (
                  <li
                    key={index}
                    className="group relative aspect-square overflow-hidden rounded-md border border-gray-200 bg-white shadow-sm"
                  >
                    <img
                      src={file.preview}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => removeNewImage(index)}
                      className="absolute top-1 right-1 flex h-6 w-6 items-center justify-center rounded-full bg-gray-900/70 text-white opacity-0 transition-opacity group-hover:opacity-100"
                      aria-label="Remove photo"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <button
            type="submit"
            disabled={processing || !data.primary_postcode_district_id}
            className="inline-flex justify-center rounded-md border border-transparent bg-orange-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50"
          >
            {profile ? "Save profile" : "Activate Tradesman Domestic"}
          </button>
        </form>

        {profile && (
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-medium text-gray-900">
              Extra postcode districts
            </h2>
            <p className="text-sm text-gray-500">
              £{monthly_price_per_postcode}/month per extra district — a
              separate Tradesman Domestic subscription, independent of any
              other subscription on your account. Removing a district keeps
              it active until your current billing period ends — no refund
              for time already paid, but you won&apos;t be charged for it
              again.
            </p>

            {subscription && subscription.has_pending && !subscription.paid && (
              <div className="mt-4 rounded-md bg-yellow-50 border border-yellow-200 p-4">
                <p className="text-sm text-yellow-800">
                  Some districts are awaiting payment — they will not be
                  active until checkout is complete.
                </p>
                <button
                  type="button"
                  onClick={resumeCheckout}
                  className="mt-2 rounded-md bg-orange-600 py-2 px-4 text-sm font-medium text-white hover:bg-orange-700"
                >
                  Complete payment
                </button>
              </div>
            )}

            {subscription &&
              !subscription.paid &&
              !subscription.has_pending &&
              extra_postcodes.length > 0 && (
                <div className="mt-4 rounded-md bg-red-50 border border-red-200 p-4">
                  <p className="text-sm text-red-800">
                    Your Domestic subscription is {subscription.status} —
                    extra districts are paused until payment is up to date.
                  </p>
                </div>
              )}

            <ul className="mt-4 divide-y divide-gray-200">
              {extra_postcodes.map((pc) => (
                <li
                  key={pc.id}
                  className="flex items-center justify-between py-3"
                >
                  <span className="text-sm text-gray-900">
                    {pc.code}
                    {pc.region ? ` — ${pc.region}` : ""}
                    {pc.status === "pending" && (
                      <span className="ml-2 inline-flex rounded-full bg-yellow-100 px-2 text-xs font-semibold leading-5 text-yellow-800">
                        awaiting payment
                      </span>
                    )}
                    {pc.status === "pending_removal" && (
                      <span className="ml-2 inline-flex rounded-full bg-gray-200 px-2 text-xs font-semibold leading-5 text-gray-700">
                        removing on {pc.scheduled_removal_at}
                      </span>
                    )}
                  </span>
                  {pc.status === "pending_removal" ? (
                    <button
                      type="button"
                      onClick={() => undoRemoval(pc.postcode_district_id)}
                      className="text-sm font-medium text-orange-600 hover:text-orange-500"
                    >
                      Undo
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => removeExtraPostcode(pc.id)}
                      className="text-sm font-medium text-red-600 hover:text-red-500"
                    >
                      Remove
                    </button>
                  )}
                </li>
              ))}
              {extra_postcodes.length === 0 && (
                <li className="py-3 text-sm text-gray-500">
                  No extra districts yet.
                </li>
              )}
            </ul>

            <div className="mt-4 flex items-end gap-3">
              <div className="flex-1">
                <AsyncSelect
                  styles={SelectStyles}
                  loadOptions={loadDistricts}
                  defaultOptions={[]}
                  placeholder="Add another KT district…"
                  value={extraPostcodeChoice}
                  onChange={setExtraPostcodeChoice}
                />
              </div>
              <button
                type="button"
                onClick={addExtraPostcode}
                disabled={!extraPostcodeChoice}
                className="rounded-md border border-transparent bg-orange-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-orange-700 disabled:opacity-50"
              >
                Add (£{monthly_price_per_postcode}/mo)
              </button>
            </div>
          </div>
        )}

        {profile && (
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900">Your reviews</h2>
              {average_rating && (
                <div className="flex items-center gap-2">
                  <StarRating value={average_rating} size="h-4 w-4" />
                  <span className="text-sm text-gray-500">
                    {average_rating.toFixed(1)} ({reviews.length})
                  </span>
                </div>
              )}
            </div>
            <p className="text-sm text-gray-500">
              Left by customers once a matched job is marked complete.
            </p>
            {reviews.length === 0 ? (
              <p className="mt-3 text-sm text-gray-500">No reviews yet.</p>
            ) : (
              <ul className="mt-4 divide-y divide-gray-200">
                {reviews.map((r) => (
                  <li key={r.id} className="py-4 first:pt-0">
                    <div className="flex items-center justify-between">
                      <StarRating value={r.rating} size="h-4 w-4" />
                      <span className="text-xs text-gray-400">{r.created_at}</span>
                    </div>
                    {r.note && (
                      <p className="mt-2 whitespace-pre-wrap text-sm text-gray-700">
                        {r.note}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ProfileSetup;
